import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormFields } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContactFormFieldProps } from 'src/components/Contact/ContactFormTypes';
import { businessPattern, cityPattern, fdmpEmailPattern, last4DigitsPattern, midPattern, namePattern, phonePattern, rfc5322CompliantEmailPattern, streetAddressPattern, usZipCodePattern } from 'src/components/Contact/regex';
import { invalidFdmpEmailDomains } from 'src/utils/InvalidFdmpEmailDomains';
import * as Yup from 'yup';

type TUseValidations = {
  getFirstNameValidation: () => {};
  getLastNameValidation: () => {};
  getBusinessNameValidation: () => {};
  getEmailAddressValidation: () => {};
  getPhoneNumberValidation: () => {};
  getAddressValidation: () => {};
  getAddressOptionalValidation: () => {};
  getCityValidation: () => {};
  getStateValidation: () => {};
  getZipCodeValidation: () => {};
  getSsnLast4Validation: () => {};
  getBankAccountLast4Validation: () => {};
  getMidValidation: () => {};
  getBusinessTypeValidation: () => {};
  getYearsInBusinessValidation: () => {};
  getGoogleRecaptchaValidation: () => {};
  getRequiredCheckboxValidation: () => {};
  getNotRequiredValidation: () => {};
  getAnnualProcessingVolumeValidation: () => {};
  getWhenToCallValidation: () => {};
};

export const FIRST_NAME_MAX_CHARS = 20;
export const LAST_NAME_MAX_CHARS = 20;

export const DEFAULT_FORM_FIELDS = [
  { fieldType: FormFields.FIRST_AND_LAST_NAME },
  { fieldType: FormFields.EMAIL },
  { fieldType: FormFields.PHONE_NUMBER },
  { fieldType: FormFields.BUSINESS_NAME },
];

export const useValidations = (fields: ContactFormFieldProps = { formFields: DEFAULT_FORM_FIELDS }): TUseValidations => {
  const { t } = useTranslation();

  const requiredMessage = t('required');
  const validEmailMessage = t('valid-email');
  const validPhoneMessage = t('valid-phone');
  const validAddressMessage = t('valid-address');
  const validCityMessage = t('valid-city');
  const validZipCodeMessage = t('valid-zip-code');
  const validSsnLast4Message = t('valid-ssn-last-4');
  const validBankAccountLast4Message = t('valid-bank-account-last-4');
  const validMidMessage = t('valid-mid');
  const validNameMessage = t('valid-name');
  const validBusinessNameMessage = t('valid-business-name');

  const hasValidEmailDomain = (email: string) => {
    const emailDomain = email && email.split('@').pop();
    return emailDomain != null && !invalidFdmpEmailDomains.has(emailDomain.toString().toLowerCase());
  };

  const getFirstNameValidation = useCallback(() => Yup.string().trim().matches(namePattern, validNameMessage).max(FIRST_NAME_MAX_CHARS).required(requiredMessage), [requiredMessage]);
  const getLastNameValidation = useCallback(() => Yup.string().trim().matches(namePattern, validNameMessage).max(LAST_NAME_MAX_CHARS).required(requiredMessage), [requiredMessage]);
  const getEmailAddressValidation = useCallback(() => Yup.string().trim().matches(fdmpEmailPattern, validEmailMessage).matches(rfc5322CompliantEmailPattern, validEmailMessage).test('invalid Email Domain', validEmailMessage, (value: string) => hasValidEmailDomain(value)).required(requiredMessage), [validEmailMessage, validEmailMessage, validEmailMessage, requiredMessage]);
  const getBusinessNameValidation = useCallback(() => Yup.string().trim().matches(businessPattern, validBusinessNameMessage).required(requiredMessage), [validBusinessNameMessage, requiredMessage]);
  const getAddressValidation = useCallback(() => Yup.string().trim().matches(streetAddressPattern, validAddressMessage).required(requiredMessage), [validAddressMessage, requiredMessage]);
  const getAddressOptionalValidation = useCallback(() => Yup.string().trim().matches(streetAddressPattern, validAddressMessage).notRequired(), [validAddressMessage]);
  const getCityValidation = useCallback(() => Yup.string().trim().matches(cityPattern, validCityMessage).required(requiredMessage), [validCityMessage, requiredMessage]);
  const getStateValidation = useCallback(() => Yup.string().required(requiredMessage), [requiredMessage]);
  const getZipCodeValidation = useCallback(() => Yup.string().matches(usZipCodePattern, validZipCodeMessage).required(requiredMessage), [validZipCodeMessage, requiredMessage]);
  const getPhoneNumberValidation = useCallback(() => Yup.string().matches(phonePattern, validPhoneMessage).required(requiredMessage), [validPhoneMessage, requiredMessage]);
  const getSsnLast4Validation = useCallback(() => Yup.string().trim().matches(last4DigitsPattern, validSsnLast4Message).required(requiredMessage), [validSsnLast4Message, requiredMessage]);
  const getBankAccountLast4Validation = useCallback(() => Yup.string().trim().matches(last4DigitsPattern, validBankAccountLast4Message).required(requiredMessage), [validBankAccountLast4Message, requiredMessage]);
  const getMidValidation = useCallback(() => Yup.string().trim().matches(midPattern, validMidMessage).required(requiredMessage), [validMidMessage, requiredMessage]);
  const getBusinessTypeValidation = useCallback(() => Yup.string().required(requiredMessage), [requiredMessage]);
  const getYearsInBusinessValidation = useCallback(() => Yup.string().required(requiredMessage), [requiredMessage]);
  const getGoogleRecaptchaValidation = useCallback(() => Yup.string().required(), []);
  const getRequiredCheckboxValidation = useCallback(() => Yup.bool().oneOf([true], requiredMessage), [requiredMessage]);
  const getNotRequiredValidation = useCallback(() => Yup.string().notRequired(), []);
  const getAnnualProcessingVolumeValidation = useCallback(() => Yup.string().required(requiredMessage), [requiredMessage]);
  const getWhenToCallValidation = useCallback(() => Yup.string().required(requiredMessage), [requiredMessage]);

  return {
    getFirstNameValidation,
    getLastNameValidation,
    getBusinessNameValidation,
    getEmailAddressValidation,
    getPhoneNumberValidation,
    getAddressValidation,
    getAddressOptionalValidation,
    getCityValidation,
    getStateValidation,
    getZipCodeValidation,
    getSsnLast4Validation,
    getBankAccountLast4Validation,
    getMidValidation,
    getBusinessTypeValidation,
    getYearsInBusinessValidation,
    getGoogleRecaptchaValidation,
    getRequiredCheckboxValidation,
    getNotRequiredValidation,
    getAnnualProcessingVolumeValidation,
    getWhenToCallValidation,
  };
};
